import { type JSX, useState } from 'react';

import { createCheckoutSession, type Plan } from '../api.js';
import { TierDetailModal, type TierId } from './TierDetailModal.js';

export interface UpgradePanelProps {
  tenantId: string;
  /** Null now — checkout authorizes via the session cookie (owner, role-gated). */
  editToken: string | null;
  /** Current tier to suppress its own column (Pro tenants see only Enterprise). */
  currentTier?: 'core' | 'pro' | 'enterprise';
}

interface PlanButton {
  plan: Plan;
  label: string;
  /** Displayed price string — these mirror the Stripe price IDs in wrangler.toml. */
  price: string;
  /** Optional annual-saves badge. */
  savings?: string;
}

interface TierColumn {
  tier: 'pro' | 'enterprise';
  name: string;
  tagline: string;
  features: string[];
  monthly: PlanButton;
  yearly: PlanButton;
}

/**
 * Displayed prices. Keep these in sync with the dashboard amounts on the
 * matching Stripe Price IDs in wrangler.toml. Updating Stripe-side does
 * not need a code change to the backend (price IDs are env-driven), but
 * the displayed dollar amount here is hard-coded — change it in one
 * place when you reprice.
 */
const TIERS: TierColumn[] = [
  {
    tier: 'pro',
    name: 'Pro',
    tagline: 'Everything in Core, plus the features fleets actually ask for.',
    features: [
      'Theming (colors, logo, wallpaper, custom font)',
      'Multi-profile + scheduled rotation',
      'Signage mode (full-screen, no chrome)',
      'Diagnostic overlay + exit gesture',
      'Broadcasts to your fleet',
      'Agent action tiles',
      'Offline-first ZIP bundle (works with any MDM)',
    ],
    monthly: { plan: 'pro_monthly', label: 'Monthly', price: '$49/mo' },
    yearly: { plan: 'pro_yearly', label: 'Yearly', price: '$499/yr', savings: 'save ~15%' },
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    tagline: 'For larger fleets, ruggedized hardware, and compliance shops.',
    features: [
      'Everything in Pro',
      'Remote config (live updates without redeploy)',
      'Telemetry export',
      'SSO display + audit log',
      'Priority support',
    ],
    monthly: { plan: 'ent_monthly', label: 'Monthly', price: '$199/mo' },
    yearly: { plan: 'ent_yearly', label: 'Yearly', price: '$1,999/yr', savings: 'save ~16%' },
  },
];

type Busy = null | Plan;

export function UpgradePanel({
  tenantId,
  editToken,
  currentTier = 'core',
}: UpgradePanelProps): JSX.Element {
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout(plan: Plan): Promise<void> {
    setError(null);
    setBusy(plan);
    const origin = window.location.origin;
    const successUrl = `${origin}/?upgraded=1&tenant=${encodeURIComponent(tenantId)}`;
    const cancelUrl = `${origin}/?canceled=1&tenant=${encodeURIComponent(tenantId)}`;
    const res = await createCheckoutSession({
      tenantId,
      editToken,
      plan,
      successUrl,
      cancelUrl,
    });
    if (res.kind === 'ok') {
      window.location.assign(res.url);
      return;
    }
    setBusy(null);
    if (res.kind === 'unauthorized') {
      setError('Edit token rejected. Re-sign up with the same email to recover.');
    } else if (res.kind === 'invalid') {
      setError(res.message);
    } else if (res.kind === 'stripe') {
      setError(`Stripe rejected: ${res.message}`);
    } else if (res.kind === 'network') {
      setError(`Network: ${res.message}`);
    } else {
      setError(res.message);
    }
  }

  const visibleTiers = TIERS.filter((t) => {
    // On Pro, hide the Pro column (suggesting Enterprise only).
    if (currentTier === 'pro') return t.tier === 'enterprise';
    return true;
  });

  const [detailTier, setDetailTier] = useState<TierId | null>(null);

  return (
    <div className="upgrade-panel" data-testid="upgrade-panel">
      {error !== null && (
        <p className="details__bundle-err" role="alert">{error}</p>
      )}
      <div className="upgrade-grid">
        {visibleTiers.map((tier) => (
          <article key={tier.tier} className={`upgrade-card upgrade-card--${tier.tier}`}>
            <header className="upgrade-card__head">
              <div className="upgrade-card__title-row">
                <h3 className="upgrade-card__name">{tier.name}</h3>
                <span className={`upgrade-card__pill upgrade-card__pill--${tier.tier}`}>
                  {tier.tier === 'pro' ? 'most popular' : 'for fleets'}
                </span>
              </div>
              <p className="upgrade-card__tagline">{tier.tagline}</p>
            </header>
            <ul className="upgrade-card__features">
              {tier.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <div className="upgrade-card__buttons">
              <PlanButton
                button={tier.monthly}
                disabled={busy !== null}
                busy={busy === tier.monthly.plan}
                onClick={() => {
                  void startCheckout(tier.monthly.plan);
                }}
              />
              <PlanButton
                button={tier.yearly}
                disabled={busy !== null}
                busy={busy === tier.yearly.plan}
                onClick={() => {
                  void startCheckout(tier.yearly.plan);
                }}
              />
            </div>
            <button
              type="button"
              className="upgrade-card__learn"
              onClick={() => setDetailTier(tier.tier)}
              data-testid={`upgrade-learn-${tier.tier}`}
            >
              Learn more about {tier.name} →
            </button>
          </article>
        ))}
      </div>
      <p className="details__hint">
        Checkout is hosted by Stripe. Your card details never touch our servers.{' '}
        <button
          type="button"
          className="upgrade-card__learn-inline"
          onClick={() => setDetailTier('core')}
          data-testid="upgrade-learn-core"
        >
          What you get on free →
        </button>
      </p>

      {detailTier !== null && (
        <TierDetailModal
          tier={detailTier}
          onClose={() => setDetailTier(null)}
        />
      )}
    </div>
  );
}

function PlanButton({
  button,
  disabled,
  busy,
  onClick,
}: {
  button: PlanButton;
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="upgrade-card__button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`upgrade-${button.plan}`}
    >
      <span className="upgrade-card__button-label">{button.label}</span>
      <span className="upgrade-card__button-price">{button.price}</span>
      {button.savings !== undefined && (
        <span className="upgrade-card__button-savings">{button.savings}</span>
      )}
      {busy && <span className="upgrade-card__button-busy">Redirecting…</span>}
    </button>
  );
}
